import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { Sliders, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { ColumbusPlannerTermModel } from '@/api/generated/schemas';
import type { TermCalculation } from '../../columbusTypes';

interface Props {
  term: ColumbusPlannerTermModel;
  calculation: TermCalculation | undefined;
  isExpanded: boolean;
  hasCustomPlan: boolean;
  rowIdx: number;
  onToggleExpand: () => void;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function TermRow({
  term,
  calculation,
  isExpanded,
  hasCustomPlan,
  rowIdx,
  onToggleExpand,
}: Props) {
  const { t } = useTranslation('columbus-planner');
  // Zebra proužkování termínů. Tinty jsou mode-aware, ruční dark: páry proto odpadly.
  const bg = rowIdx % 2 === 0 ? 'bg-phx-surface-2' : 'bg-phx-warning-tint';

  return (
    <div
      className={cn(
        'border-t-phx-border gap-phx-4 flex items-center border-t border-solid px-2 py-0.5 text-xs whitespace-nowrap',
        bg
      )}
    >
      {/* Date + nights */}
      <strong className="w-28 flex-initial">
        {formatDate(term.dateFrom)}
        {term.nights != null && term.nights > 0 ? ` / ${term.nights}n` : null}
      </strong>

      {/* Breakpoint */}
      <div className="w-5 flex-auto text-right">{calculation?.breakpoint ?? ''}</div>

      {/* Pax: sold / total capacity */}
      <div className="w-14 flex-auto text-right">
        {calculation != null
          ? `${(term.passengers ?? []).length}/${calculation.totalCapacity}`
          : ''}
      </div>

      {/* Current profit */}
      <div className="w-16 flex-auto text-right">{calculation?.currentProfit ?? 0}</div>

      {/* Max profit */}
      <div className="w-16 flex-auto text-right">{calculation?.maxProfit ?? 0}</div>

      {/* Total group costs */}
      <div className="w-16 flex-auto text-right">{calculation?.totalGroupCosts ?? 0}</div>

      {/* Per pax: group cost / sold pax + pax costs */}
      <div className="w-24 flex-auto">
        {calculation != null &&
        (calculation.currentPaxGroupCost != null || calculation.totalPaxCosts > 0)
          ? [
              calculation.currentPaxGroupCost != null
                ? String(calculation.currentPaxGroupCost)
                : null,
              calculation.totalPaxCosts > 0 ? String(calculation.totalPaxCosts) : null,
            ]
              .filter(Boolean)
              .join(' + ')
          : ''}
      </div>

      {/* Manual edit indicator */}
      <div className="w-4">
        {hasCustomPlan && <Sliders className="text-phx-text-muted h-3 w-3" />}
      </div>

      {/* Expand button */}
      <div className="w-6 flex-initial">
        <button
          type="button"
          className={cn(
            'hover:bg-phx-surface-3 rounded p-0.5 transition-colors',
            isExpanded ? 'text-phx-primary' : 'text-phx-text-muted'
          )}
          onClick={onToggleExpand}
          title={isExpanded ? t('termRow.hideDetail') : t('termRow.showDetail')}
        >
          {isExpanded ? (
            <ChevronsLeft className="h-3 w-3" />
          ) : (
            <ChevronsRight className="h-3 w-3" />
          )}
        </button>
      </div>
    </div>
  );
}
